import { useEffect, useRef, useState } from 'react';
import { Chip, Tooltip, makeStyles } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import SettingsInputAntennaIcon from '@material-ui/icons/SettingsInputAntenna';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { WorkerStatus } from '../../api/types';
import { useWorkerStatus } from '../../hooks/useWorkerStatus';
import type { WorkerPresentation } from '../../hooks/useWorkerStatus';

const POLL_INTERVAL_MS = 15_000;

/**
 * A classificação das quatro condições e o descarte de resposta obsoleta vivem
 * em `hooks/useWorkerStatus`, compartilhados com o modal de escolha da via
 * (ADR-004). Este componente é só a apresentação no cabeçalho: ele nomeia o
 * Frontier Worker em todo rótulo e nunca lê, altera ou agrega estado de
 * atividade Control Plane — as duas coisas são independentes (US-013).
 */
export type { WorkerPresentation };

const LABELS: Record<WorkerPresentation, string> = {
  loading: 'Frontier Worker: carregando',
  connected: 'Frontier Worker: conectado',
  disconnected: 'Frontier Worker: desconectado',
  unavailable: 'Frontier Worker: estado indisponível',
};

/**
 * Ícone por condição, em mapa e não em ternária encadeada: o `.eslintrc.js` do
 * repositório proíbe ternária aninhada, e um mapa deixa a exaustividade sob a
 * checagem de tipo.
 */
const ICONS: Record<WorkerPresentation, JSX.Element> = {
  loading: <HelpOutlineIcon style={{ color: 'inherit' }} />,
  connected: <SettingsInputAntennaIcon style={{ color: 'inherit' }} />,
  disconnected: <SettingsInputAntennaIcon style={{ color: 'inherit' }} />,
  unavailable: <WarningIcon style={{ color: 'inherit' }} />,
};

const useStyles = makeStyles(theme => ({
  chip: {
    fontWeight: 500,
    fontSize: '0.75rem',
    marginRight: theme.spacing(1),
    maxWidth: 260,
  },
  connected: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
  },
  disconnected: {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.common.white,
  },
  unavailable: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  loading: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
  },
}));

export { presentationFor } from '../../hooks/useWorkerStatus';

/**
 * Long detail belongs in the tooltip; the chip label stays short so the header
 * layout never shifts (UT-036).
 */
export function tooltipFor(
  presentation: WorkerPresentation,
  status: WorkerStatus | null,
): string {
  if (presentation === 'loading') {
    return 'Frontier Worker: consultando o estado da conexão.';
  }
  if (presentation === 'unavailable') {
    return 'Frontier Worker: não foi possível determinar o estado. Isto não significa que o worker está desconectado.';
  }
  if (presentation === 'disconnected') {
    return 'Frontier Worker: nenhum worker local conectado. Verifique se o Frontier Worker está em execução. O estado das atividades Control Plane não é afetado.';
  }
  const version = status?.workerVersion || '?';
  const since = status?.connectedAt
    ? new Date(status.connectedAt).toLocaleString('pt-BR')
    : '?';
  const outdated = status?.outdated
    ? ' Versão desatualizada: execute "yarn worker:update".'
    : '';
  return `Frontier Worker v${version} conectado desde ${since}.${outdated}`;
}

export const WorkerStatusIndicator = () => {
  const classes = useStyles();
  const [announcement, setAnnouncement] = useState('');
  const lastAnnounced = useRef<WorkerPresentation | null>(null);
  const { presentation, status } = useWorkerStatus({
    pollIntervalMs: POLL_INTERVAL_MS,
  });

  useEffect(() => {
    // Announce transitions only: repeated polls in the same state stay silent so
    // a screen reader is not interrupted every interval (UT-039).
    if (lastAnnounced.current === presentation) return;
    lastAnnounced.current = presentation;
    setAnnouncement(LABELS[presentation]);
  }, [presentation]);

  return (
    <>
      <Tooltip title={tooltipFor(presentation, status)} arrow>
        <Chip
          icon={ICONS[presentation]}
          label={LABELS[presentation]}
          size="small"
          className={`${classes.chip} ${classes[presentation]}`}
          data-testid={`worker-${presentation}`}
          aria-label={LABELS[presentation]}
        />
      </Tooltip>
      <span
        aria-live="polite"
        aria-atomic="true"
        aria-label="Estado do Frontier Worker"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}
      >
        {announcement}
      </span>
    </>
  );
};
